/**
 * Who changed what.
 *
 * Procurement asks whether this exists; a surveyor asks to see it. Both
 * questions are answered by the same page, so it is built to be READ rather
 * than skimmed — every row says who, what, when, and against which document,
 * with no interpretation in between.
 *
 * Entries are never editable and never deletable. That is enforced by the
 * backend and by the rules; the absence of any control here is deliberate
 * rather than unfinished.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../auth/useAuth';
import { call, isCallFailure } from '../../lib/callable';
import { Spinner } from '../../app/Spinner';
import { PageState } from '../../shared/PageState';
import styles from '../../shared/Page.module.css';
import own from './Admin.module.css';

/** The Who column for work nobody did by hand: a scheduled job, a trigger. */
export const AUTOMATIC_ACTOR = 'StandingDocs (automatic)';
/** Older automatic entries carry this made-up address; the log cannot be edited, so it is translated here. */
const LEGACY_SYSTEM_EMAIL = 'system@somexai.com';

/**
 * Who did it. A person is named by their ADDRESS, which is their account:
 * a display name can be shared or changed, and "System Admin" is a role, not
 * a person. The name goes underneath when there is one. Automatic work says
 * so in words and shows no address, because there is none.
 */
export function whoOf(entry: Pick<AuditEntry, 'actorEmail' | 'actorName' | 'actorDisplayName' | 'actorType' | 'actorId'>): { primary: string | null; secondary: string | null; automatic: boolean } {
  const automatic = entry.actorId === 'system' || entry.actorType === 'system' || entry.actorEmail === LEGACY_SYSTEM_EMAIL;
  if (automatic) return { primary: AUTOMATIC_ACTOR, secondary: null, automatic: true };
  const name = (entry.actorDisplayName || entry.actorName || '').trim() || null;
  const email = (entry.actorEmail || '').trim() || null;
  return { primary: email ?? name, secondary: email && name && name !== email ? name : null, automatic: false };
}

interface AuditEntry {
  id?: string;
  action?: string;
  actorEmail?: string | null;
  actorName?: string | null;
  actorDisplayName?: string | null;
  actorType?: string | null;
  actorId?: string;
  resourceType?: string;
  resourceName?: string;
  resourceId?: string;
  sensitivity?: string;
  timestamp?: unknown;
}

interface AuditResponse {
  logs?: AuditEntry[];
  entries?: AuditEntry[];
  cursor?: string | null;
  hasMore?: boolean;
}

/** A Firestore Timestamp, ISO string or epoch, as a readable local time. */
function formatWhen(value: unknown): string {
  let date: Date | null = null;

  if (typeof value === 'string') date = new Date(value);
  else if (typeof value === 'number') date = new Date(value);
  else if (typeof value === 'object' && value !== null) {
    const v = value as { toDate?: () => Date; _seconds?: number; seconds?: number };
    if (typeof v.toDate === 'function') date = v.toDate();
    else if (typeof v._seconds === 'number') date = new Date(v._seconds * 1000);
    else if (typeof v.seconds === 'number') date = new Date(v.seconds * 1000);
  }

  if (!date || Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
}

/** `policy.published` reads better as "policy published". */
function humanAction(action: string | undefined): string {
  if (!action) return 'Unknown action';
  return action.replace(/[._]/g, ' ');
}

function sensitivityTone(level: string | undefined): string {
  if (level === 'critical') return styles.danger ?? '';
  if (level === 'elevated') return styles.warn ?? '';
  return styles.neutral ?? '';
}

const SENSITIVITIES = ['', 'standard', 'elevated', 'critical'] as const;

export function AuditPage() {
  const { session } = useAuth();
  const [sensitivity, setSensitivity] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['audit-logs', session?.orgId, sensitivity],
    enabled: Boolean(session?.orgId),
    retry: false,
    queryFn: () =>
      call<Record<string, unknown>, AuditResponse>('getAuditLogs', {
        limit: 200,
        ...(sensitivity ? { sensitivity } : {}),
      }),
  });

  if (isLoading) return <Spinner label="Loading the audit log" />;

  if (error) {
    return (
      <PageState title="Could not load the audit log" tone="alert">
        {isCallFailure(error) ? error.message : 'Something went wrong.'}
      </PageState>
    );
  }

  // The callable has returned this under both names over its life; reading
  // either avoids an empty page if it changes again.
  const entries = data?.logs ?? data?.entries ?? [];

  return (
    <>
      <header className={styles.header}>
        <h1 className={styles.title}>Audit log</h1>
        <p className={styles.subtitle}>
          Every change made in this organization, in the order it happened.
          Entries cannot be edited or deleted by anyone, including an
          administrator — that is what makes the log worth showing a surveyor.
        </p>
      </header>

      <div className={styles.body}>
        <div className={own.bar}>
          <div style={{ flex: 1 }}>
            <label className={own.label} htmlFor="audit-sensitivity">
              Show
            </label>
            <select
              id="audit-sensitivity"
              className={own.input}
              style={{ maxWidth: 260 }}
              value={sensitivity}
              onChange={(e) => setSensitivity(e.target.value)}
            >
              {SENSITIVITIES.map((s) => (
                <option key={s} value={s}>
                  {s === '' ? 'Everything' : `${s[0]!.toUpperCase()}${s.slice(1)} only`}
                </option>
              ))}
            </select>
          </div>
          <div className={own.count} role="status" aria-live="polite">
            <b>{entries.length}</b> {entries.length === 1 ? 'entry' : 'entries'}
          </div>
        </div>

        {entries.length === 0 ? (
          <div className={`${styles.notice} ${styles.noticeInfo}`} role="status">
            No audit entries match. Activity appears here as soon as anyone
            changes something.
          </div>
        ) : (
          <div className={own.scroller}>
            <table className={own.table}>
              <caption>
                Changes in this organization, most recent first
                {sensitivity && ` — ${sensitivity} sensitivity only`}
              </caption>
              <thead>
                <tr>
                  <th scope="col">When</th>
                  <th scope="col">Who</th>
                  <th scope="col">Did what</th>
                  <th scope="col">To what</th>
                  <th scope="col">Sensitivity</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry, i) => (
                  <tr key={entry.id ?? i}>
                    <td className="tnum" style={{ whiteSpace: 'nowrap' }}>
                      {formatWhen(entry.timestamp)}
                    </td>
                    <td>
                      {(() => {
                        const who = whoOf(entry);
                        if (!who.primary) return <span className={styles.absent}>&mdash;</span>;
                        return (
                          <>
                            <span style={who.automatic ? { color: 'var(--muted)' } : undefined}>{who.primary}</span>
                            {who.secondary && <div className={styles.itemMeta}>{who.secondary}</div>}
                          </>
                        );
                      })()}
                    </td>
                    <td style={{ fontWeight: 500 }}>{humanAction(entry.action)}</td>
                    <td style={{ color: 'var(--ink-2)' }}>
                      {entry.resourceName || entry.resourceType || (
                        <span className={styles.absent}>&mdash;</span>
                      )}
                    </td>
                    <td>
                      <span className={`${own.pill} ${sensitivityTone(entry.sensitivity)}`}>
                        {entry.sensitivity ?? 'standard'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {data?.hasMore && (
          <p className={styles.absent} style={{ marginTop: 'var(--sp-4)' }}>
            Showing the most recent 200 entries. Export is not built yet.
          </p>
        )}
      </div>
    </>
  );
}
